import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Avatar,
  Card,
  Skeleton,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Constants & assets
import { popupTimer, API_URL } from "../../consts";
import social1 from "../../Assets/social_1.svg";
import social2 from "../../Assets/social_2.svg";
import social3 from "../../Assets/social_3.svg";
import social4 from "../../Assets/social_4.svg";
import social5 from "../../Assets/social_5.svg";
import ai from "../../Assets/ai.png";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';

// Child components
import AiSummaryEditor from "../Ai/AiSummaryEditor";
import AiDialog from "../Ai/AiDialog";
import CreateSpace from "./CreateSpace";
import SpaceDescription from "./SpaceDescription";
import SettingsDialog from "../common/SettingsDialog";
import ConfirmationDialog from "../common/ConfirmationDialog";
import SpaceRates from "./SpaceRates";
import ChatComponent from "../AiChat/ChatComponent";
import RatingObject from "../RatingObject/RatingObject";
import AnalyticsDialog from "./AnalyticsDialog";
// Styles
import "./style.css";

const Space = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const setTokenInProcess = useSelector((state) => state.setTokenInProcess);

  const collobartorsDialogOpen = useSelector((state) => state.collobartorsDialogOpen);
  const deleteSpaceDialogOpen = useSelector((state) => state.deleteSpaceDialogOpen);
  const aiChatResponse = useSelector((state) => state.aiChatResponse);
  const reloadCurrentSpace = useSelector((state) => state.reloadCurrentSpace);

  // Local state
  const [spaceData, setSpaceData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [socialCard, setSocialCard] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [summary, setSummary] = useState("");
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [subSpaces, setSubSpaces] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(null);
  // Description card collapsed by default
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [triggerEdit, setTriggerEdit] = useState(false);
  
  // AI Chat integration
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const spaceRatesRef = useRef(null);
  
  // Analytics dialog state
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'feed'
  const previousViewModeRef = useRef(null); // Store previous view mode when chat opens
  const previousViewModeForResizeRef = useRef(null); // Store previous view mode when resize forces feed
  const containerRef = useRef(null); // Ref for the container to measure card width

  // Get chat open state from Redux
  const chatOpen = useSelector((state) => state.chatOpen);
  
  // Tab state - replace recommendations checkbox with tabs
  // Initialize from URL parameter
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'recommendations') return 1;
    return 0; // Default to Saved Items
  };
  const [activeTab, setActiveTab] = useState(getInitialTab()); // 0 = Saved Items, 1 = Recommendations
  
  // Drag and drop states
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showRatingObjectDialog, setShowRatingObjectDialog] = useState(false);
  const [draggedItemData, setDraggedItemData] = useState(null);
  
  // Handler for tab change
  const handleTabChange = (event, newValue) => {
    // Check if user has permission to view recommendations tab
    const canViewRecommendations = spaceData.user_permission?.includes('manage_collections');
    if (newValue === 1 && !canViewRecommendations) {
      // User doesn't have permission, stay on saved items tab
      return;
    }
    setActiveTab(newValue);
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 1) {
      newSearchParams.set('tab', 'recommendations');
    } else {
      newSearchParams.delete('tab'); // Default is saved items, so remove param
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Sync URL with activeTab changes
  useEffect(() => {
    const currentTabParam = searchParams.get('tab');
    const expectedTabParam = activeTab === 1 ? 'recommendations' : null;
    
    // Only update URL if it's different from current state
    if ((expectedTabParam === 'recommendations' && currentTabParam !== 'recommendations') ||
        (expectedTabParam === null && currentTabParam === 'recommendations')) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (activeTab === 1) {
        newSearchParams.set('tab', 'recommendations');
      } else {
        newSearchParams.delete('tab');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [activeTab]);

  // Sync activeTab with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const expectedTab = tabParam === 'recommendations' ? 1 : 0;
    // Check if user has permission to view recommendations tab
    const canViewRecommendations = spaceData.user_permission?.includes('manage_collections');
    if (expectedTab === 1 && !canViewRecommendations) {
      // User doesn't have permission, force to saved items tab
      setActiveTab(0);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tab');
      setSearchParams(newSearchParams, { replace: true });
    } else if (activeTab !== expectedTab) {
      setActiveTab(expectedTab);
    }
  }, [searchParams, spaceData.user_permission]);

  // On initial space load, default to the Recommendations tab when AI
  // recommendations are enabled (and the user has permission to view it).
  // Runs once, and never overrides an explicit `tab` param in the URL.
  const hasSetInitialTabRef = useRef(false);
  useEffect(() => {
    if (hasSetInitialTabRef.current) return;
    // Wait until space data has actually loaded
    if (!spaceData || Object.keys(spaceData).length === 0) return;
    hasSetInitialTabRef.current = true;

    // Respect an explicit tab selection coming from the URL
    if (searchParams.get('tab')) return;

    const canViewRecommendations = spaceData.user_permission?.includes('manage_collections');
    if (spaceData.search_for_recommendations && canViewRecommendations) {
      setActiveTab(1);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'recommendations');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [spaceData]);

  // No localStorage persistence needed for description expansion state

  // Clear global AI filter on mount/unmount so it doesn't bleed into other pages
  useEffect(() => {
    dispatch({ type: 'FILTER_RATES', payload: null });
    return () => {
      dispatch({ type: 'FILTER_RATES', payload: null });
    };
  }, []);

  // ---------------------------
  // Fetch the Space metadata
  // ---------------------------
  useEffect(() => {
    if (id) {
      // Only fetch space data if we're not in the process of loading user data
      if (!setTokenInProcess) {
        getSpace();
        // Check subscription status if user is authenticated
        if (token && user) {
          checkSubscriptionStatus();
        }
      }
    }
  }, [id, setTokenInProcess, token, user]);

  // ---------------------------
  // Fetch subscriber count for public spaces
  // ---------------------------
  useEffect(() => {
    const fetchSubscriberCount = async () => {
      if (!id || !spaceData?.is_public) {
        setSubscriberCount(null);
        return;
      }

      // Only fetch if display_followers_count is not explicitly false
      const displayFollowers = spaceData.display_followers_count !== false;
      if (!displayFollowers) {
        setSubscriberCount(null);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/spaces/spaces/${id}/subscriber-count`);
        if (response.data && response.data.subscriber_count !== undefined) {
          setSubscriberCount(response.data.subscriber_count);
        }
      } catch (err) {
        // Silently fail - don't show error for subscriber count
        // 403 means count is hidden, 404 means space not found or not public
        if (err?.response?.status !== 403 && err?.response?.status !== 404) {
          console.warn('Failed to fetch subscriber count:', err);
        }
        setSubscriberCount(null);
      }
    };

    fetchSubscriberCount();
  }, [id, spaceData?.is_public, spaceData?.display_followers_count]);

  // ---------------------------
  // Listen for AI chat responses from global ChatComponent
  // ---------------------------
  useEffect(() => {
    if (aiChatResponse) {
      handleAiChatResponse(aiChatResponse);
      // Clear the response from Redux store
      dispatch({ type: "AI_CHAT_RESPONSE", payload: null });
    }
  }, [aiChatResponse, dispatch]);

  // ---------------------------
  // Listen for space reload trigger from ChatComponent
  // ---------------------------
  useEffect(() => {
    if (reloadCurrentSpace && reloadCurrentSpace.spaceId === id) {
      // Reload the space data
      getSpace();
      
      // Clear the trigger from Redux store
      dispatch({ type: "RELOAD_CURRENT_SPACE", payload: null });
    }
  }, [reloadCurrentSpace, id, dispatch]);

  // ---------------------------
  // Check subscription status
  // ---------------------------
  const checkSubscriptionStatus = async () => {
    if (!token || !user || !id) return;
    
    setIsCheckingSubscription(true);
    try {
      const response = await axios.get(`${API_URL}/spaces/authenticated/spaces/${id}/subscribe`);
      setIsSubscribed(response.data.subscribed);
    } catch (err) {
      console.error('Error checking subscription status:', err);
      // Default to false if there's an error
      setIsSubscribed(false);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  const getSpace = () => {
    setIsLoading(true);
    // Only use authenticated endpoint if we have both token and user data
    const url = (token && user)
      ? `${API_URL}/spaces/authenticated/spaces/${id}`
      : `${API_URL}/spaces/${id}`;
      
    axios
      .get(url)
      .then((res) => {
        const { space } = res.data;
        setSpaceData(space);
        setSubSpaces(space.subspaces || []);
        // Only set collaborators if they exist in the response (user has manage_collections permission)
        setCollaborators(space.collaborators || []);
        setIsLoading(false);
        
        // Only update sidebar if user is authenticated
        if (token && user) {
          dispatch({
            type: "SET_CURRENT_SPACE",
            payload: {
              currentSpace: {
                ...space,
                childrens: space.subspaces || [],
                has_children: (space.subspaces && space.subspaces.length > 0) || space.has_children || false,
                parent_space_id: space.parent_space_id
              }
            }
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  };

  // ---------------------------
  // Remove or Delete space
  // ---------------------------
  const deleteSpace = () => {
    const url = `${API_URL}/spaces/${id}`;
    axios
      .delete(url)
      .then((res) => {
        // refresh the list of spaces in Redux or navigate away
        dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });
        navigate("/");
        
        // Use the message from backend response if available, otherwise fallback to default
        const successMessage = res.data?.message || 
          (spaceData?.has_children || (subSpaces && subSpaces.length > 0)
            ? "Space and all sub-collections removed successfully"
            : "Space removed successfully");
        
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: successMessage,
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to remove space. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  // ---------------------------
  // Update space settings
  // ---------------------------
  const handleChangeSpace = (updates) => {
    // e.g. toggling is_public, is_community, etc.
    const url = `${API_URL}/spaces/${id}`;
    axios
      .put(url, updates)
      .then(() => {
        setSpaceData((prev) => ({ ...prev, ...updates }));
        // Refresh space data with a small delay to ensure server has processed changes
        setTimeout(() => {
          getSpace();
        }, 500); // 0.5 second delay
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  };

  // ---------------------------
  // Image / Video Uploads
  // ---------------------------
  const handleRemoveImage = () => {
    const url = `${API_URL}/spaces/${id}`;
    axios
      .put(url, { image_url: "" })
      .then(() => {
        setSpaceData((prev) => ({ ...prev, image: "" }));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  };

  const handleImageUpload = (stagedImageFile) => {
    // Endpoint: /spaces/:id/image
    const url = `${API_URL}/spaces/${id}/image`;
    const reader = new FileReader();

    reader.onload = () => {
      axios
        .post(url, reader.result, {
          headers: { "Content-Type": stagedImageFile.type },
        })
        .then(() => {
          // Optionally re-fetch space or update local state 
          // with the new image path.
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            dispatch({ type: "SET_UNAUTHORIZED" });
            return;
          }
        });
    };

    reader.onerror = () => {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "File reading failed. Please try another image.",
          type: "error",
        },
      });
    };

    reader.readAsArrayBuffer(stagedImageFile);
  };

  const handleVideoUpload = (videoUrl) => {
    const url = `${API_URL}/spaces/${id}`;
    axios
      .put(url, { video_url: videoUrl })
      .then(() => {
        setSpaceData((prev) => ({ ...prev, video_url: videoUrl }));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  };

  // ---------------------------
  // Collaborators
  // ---------------------------
  const addCollaborator = (email, subSpaceOptions) => {
    // Check if user has manage_collections permission
    if (!spaceData.user_permission?.includes('manage_collections')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You don't have permission to add collaborators",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      return;
    }

    const url = `${API_URL}/spaces/${id}/collaborators`;
    const payload = {
      collaborators: [email],
      ...(subSpaceOptions && {
        sub_spaces_visibility: subSpaceOptions.sub_spaces_visibility,
        selected_sub_spaces: subSpaceOptions.selected_sub_spaces
      })
    };
    
    axios
      .put(url, payload)
      .then(() => {
        // refetch space collaborators
        getSpace();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        const errorMessage = err?.response?.data?.error || "Failed to add collaborator";
        dispatch({
          type: "SET_POPUP",
          payload: { text: errorMessage, type: "error" },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  const deleteCollaborators = (collaborators) => {
    // Check if user has manage_collections permission
    if (!spaceData.user_permission?.includes('manage_collections')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You don't have permission to remove collaborators",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      return;
    }

    const url = `${API_URL}/spaces/${id}/collaborators`;
    axios
      .put(url, { collaborators: [collaborators], delete: true })
      .then(() => {
        // refetch space collaborators
        getSpace();
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Collaborator removed successfully",
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  // ---------------------------
  // AI actions
  // ---------------------------
  const performAiAction = (inputCommand) => {
    const url = `${API_URL}/rates/ai`;
    axios
      .post(url, { space_id: id, command: inputCommand.inputValue })
      .then((resp) => {
        const updatedSummary = 
          summary + 
          "\n" + 
          `<h1>${inputCommand.inputValue}</h1>` + 
          "\n" + 
          resp.data.summary;

        setSummary(updatedSummary);
        setSummaryDialogOpen(true);
      })
      .catch((err) => {
        // handle error
        console.error(err);
      });
  };

  const showComingSoonMsg = () => {
    dispatch({
      type: "SET_POPUP",
      payload: { text: "Coming Soon", type: "success" },
    });
    setTimeout(() => {
      dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
    }, popupTimer);
  };

  const toggleDescriptionExpansion = () => {
    setIsDescriptionExpanded((prev) => !prev);
  };

  // Handle AI chat response that contains filtered rating IDs
  const handleAiChatResponse = (response) => {
    // Check if the response contains filtered rating IDs and is in data_filter mode
    if (response && response.mode === "data_filter" && response.rates && Array.isArray(response.rates) && response.rates.length > 0) {
      // Apply the AI filter to SpaceRates
      if (spaceRatesRef.current) {
        spaceRatesRef.current.applyAiFilter(response.rates);
      } else {
        console.error('SpaceRates ref is not available');
      }
      
      // Close the AI chat after applying filter
      setAiChatOpen(false);
      
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: `AI Filter Applied - ${response.rates.length} items found`,
          type: "success",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    } else if (response && response.summary) {
      // Handle regular AI response without filtering
      setAiChatOpen(false);
    }
  };

  // Handle when AI filter is applied/cleared
  const handleAiFilterApplied = (ratingIds) => {
  };

  // Handle view mode change
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Handle chat open/close - switch to feed view when chat opens, restore previous when closes
  useEffect(() => {
    if (chatOpen) {
      // Chat opened - save current view mode if not already feed, then switch to feed
      if (viewMode !== 'feed') {
        previousViewModeRef.current = viewMode;
        setViewMode('feed');
      }
    } else {
      // Chat closed - restore previous view mode if it wasn't already feed
      // But only if card width allows it (checked by resize observer)
      if (previousViewModeRef.current && previousViewModeRef.current !== 'feed') {
        // Check if we can restore (card width >= 200px)
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const padding = 32;
          const spacing = 24;
          let cardWidth;
          if (containerWidth >= 960) {
            cardWidth = (containerWidth - padding - spacing * 2) / 3;
          } else if (containerWidth >= 600) {
            cardWidth = (containerWidth - padding - spacing) / 2;
          } else {
            cardWidth = containerWidth - padding;
          }
          
          if (cardWidth >= 200) {
            setViewMode(previousViewModeRef.current);
            previousViewModeRef.current = null;
          }
        }
      }
    }
  }, [chatOpen, viewMode]);

  // Handle resize - switch to feed view when card width < 200px
  useEffect(() => {
    if (!containerRef.current || viewMode === 'feed') return;

    const calculateCardWidth = () => {
      const container = containerRef.current;
      if (!container) return null;

      const containerWidth = container.clientWidth;
      const padding = 32; // Approximate padding (16px on each side)
      const spacing = 24; // Grid spacing

      // Determine breakpoint based on container width
      let cardWidth;
      if (containerWidth >= 960) {
        // md breakpoint: 3 columns
        cardWidth = (containerWidth - padding - spacing * 2) / 3;
      } else if (containerWidth >= 600) {
        // sm breakpoint: 2 columns
        cardWidth = (containerWidth - padding - spacing) / 2;
      } else {
        // xs breakpoint: 1 column
        cardWidth = containerWidth - padding;
      }

      return cardWidth;
    };

    const checkCardWidth = () => {
      const cardWidth = calculateCardWidth();
      if (cardWidth === null) return;

      if (cardWidth < 200 && viewMode !== 'feed') {
        // Card too small - switch to feed view
        if (!previousViewModeForResizeRef.current) {
          previousViewModeForResizeRef.current = viewMode;
        }
        setViewMode('feed');
      } else if (cardWidth >= 200 && previousViewModeForResizeRef.current) {
        // Card width restored - restore previous view mode
        setViewMode(previousViewModeForResizeRef.current);
        previousViewModeForResizeRef.current = null;
      }
    };

    // Check on mount and resize
    checkCardWidth();

    const resizeObserver = new ResizeObserver(() => {
      checkCardWidth();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [viewMode]);

  // Handle AI recommendations toggle
  const handleToggleRecommendations = (event) => {
    // Check if user has manage_collections permission
    if (!spaceData.user_permission?.includes('manage_collections')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You don't have permission to modify AI recommendations settings",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      return;
    }

    const newValue = event.target.checked;
    const url = `${API_URL}/spaces/${id}`;
    const payload = { search_for_recommendations: newValue };
    
    axios
      .put(url, payload)
      .then(() => {
        setSpaceData((prev) => ({ ...prev, search_for_recommendations: newValue }));
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: newValue ? "AI recommendations enabled successfully" : "AI recommendations disabled",
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
        
        // If enabling, switch to recommendations tab
        if (newValue) {
          setActiveTab(1);
        }
      })
      .catch((err) => {
        console.error('Error toggling AI recommendations:', err);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to update AI recommendations. Please try again later.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  // Handle drag and drop from recommendations to saved items
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only allow drop on Saved Items tab when viewing Recommendations
    if (activeTab === 1) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    try {
      const draggedData = e.dataTransfer.getData('application/json');
      if (draggedData) {
        const dragData = JSON.parse(draggedData);
        
        // Extract the full item data from the drag data structure
        // The structure is: { type: "rated-object", ratedObject: { id, fullData } }
        let itemData = null;
        if (dragData.ratedObject && dragData.ratedObject.fullData) {
          itemData = dragData.ratedObject.fullData;
        } else if (dragData.fullData) {
          itemData = dragData.fullData;
        } else {
          itemData = dragData;
        }
        
        if (!itemData) {
          console.error('Could not extract item data from drag data:', dragData);
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "Failed to open item. Invalid data.",
              type: "error",
            },
          });
          setTimeout(() => {
            dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
          }, popupTimer);
          return;
        }
        
        // Open the RatingObject dialog with the item data and current space pre-selected
        setDraggedItemData(itemData);
        setShowRatingObjectDialog(true);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Failed to open item. Please try again.",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    }
  };

  // Calculate sidebar width based on expansion state - REMOVED, no longer needed
  // const sidebarWidth = isSidebarExpanded ? 300 : 60;

  return (
    <>
      {/* Example "social integrations" card */}
      {socialCard && (
        <Card className="cardIntegration">
          <Button
            onClick={() => setSocialCard(false)}
            color="primary"
            sx={{ position: "absolute", right: "0" }}
          >
            X
          </Button>
          <Typography
            gutterBottom
            variant="h5"
            sx={{
              fontSize: "22px",
              textAlign: "center",
              fontWeight: "500",
              marginTop: "30px",
              marginBottom: "32px",
            }}
          >
            Integrations
          </Typography>
          {[
            { label: "Jira", img: social1, connected: true },
            { label: "Monday", img: social2, connected: true },
            { label: "Slack", img: social3, connected: false },
            { label: "Trello", img: social4, connected: false },
            { label: "Asana", img: social5, connected: false },
          ].map((item, index) => (
            <Box
              sx={{ display: "flex", marginBottom: "24px", padding: "0px 30px" }}
              key={index}
            >
              <Avatar sx={{ width: "34px", height: "34px" }} src={item.img} />
              <Typography
                gutterBottom
                variant="h5"
                sx={{
                  fontSize: "18px",
                  textAlign: "center",
                  fontWeight: "500",
                  marginLeft: "14px",
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
              >
                {item.label}
              </Typography>
              <Button
                color="primary"
                sx={{ marginLeft: "auto" }}
                className={item.connected ? "connectedButton" : "connectButton2"}
                variant="contained"
                disabled={item.connected}
                onClick={showComingSoonMsg}
              >
                {item.connected ? "Connected" : "Connect"}
              </Button>
            </Box>
          ))}
        </Card>
      )}

      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
        pt: 3,
        px: { xs: 1, sm: 2, md: 3 }
      }}>
        <Box ref={containerRef} sx={{ maxWidth: '1400px', mx: 'auto' }}>
          {isLoading ? (
            <>
              {/* Loading skeleton for the single-column layout */}
              <Box sx={{ p: 2 }}>
                {/* Space Description Skeleton */}
                <Box sx={{ mb: 3 }}>
                  <Paper sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      backgroundColor: 'rgba(25, 118, 210, 0.02)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }}>
                      <Skeleton variant="text" height={28} width="150px" />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </Paper>
                </Box>
                
                {/* SpaceRates Skeleton */}
                <Grid container spacing={2}>
                  {[1, 2, 3, 4, 5, 6].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                      <Paper sx={{ p: 2, height: 200, borderRadius: 2 }}>
                        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
                        <Skeleton variant="text" height={24} width="80%" sx={{ mb: 1 }} />
                        <Skeleton variant="text" height={20} width="60%" />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </>
          ) : (
            <>
              {/* Main content area - takes full width */}
              <Box sx={{ 
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                p: 1
              }}>
              {/* Space Description - Collapsible and collapsed by default */}
              {Object.keys(spaceData).length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Paper
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {/* Header with toggle and space preview */}
                    <Box
                      onClick={toggleDescriptionExpansion}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1, sm: 1.5 },
                        gap: { xs: 1, sm: 0 },
                        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0.08) 100%)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, rgba(25, 118, 210, 0.12) 100%)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0, flex: 1, width: '100%' }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            bgcolor: 'primary.main',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 600,
                            flexShrink: 0,
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                          }}
                          src={spaceData?.image || undefined}
                        >
                          {(spaceData?.name || 'S').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: { xs: '1rem', sm: '1.25rem' },
                              fontWeight: 700,
                              color: 'primary.main',
                              mb: 0.25,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {spaceData?.name || 'Space'}
                          </Typography>
                          {spaceData?.user_name && (
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, flexWrap: 'wrap', gap: 0.5, alignItems: { xs: 'center', sm: 'flex-start' } }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  lineHeight: 1.4
                                }}
                              >
                                Created by{' '}
                                <Box
                                  component="span"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    navigate(`/user/${spaceData.user_name}`);
                                  }}
                                  sx={{
                                    color: '#1976d2',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      color: '#1565c0',
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {spaceData.display_name ||
                                   (spaceData.first_name && spaceData.last_name
                                     ? `${spaceData.first_name} ${spaceData.last_name}`
                                     : spaceData.first_name || spaceData.last_name || spaceData.user_name)}
                                </Box>
                              </Typography>
                              {spaceData?.is_public && spaceData?.display_followers_count !== false && typeof subscriberCount === 'number' && (
                                <Chip
                                  icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
                                  label={`${subscriberCount} ${subscriberCount === 1 ? 'follower' : 'followers'}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    color: '#1976d2',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    width: 'fit-content',
                                    '& .MuiChip-icon': {
                                      color: '#1976d2',
                                    },
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          {!isDescriptionExpanded && spaceData?.description && !isMobile && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.85rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                                mt: 0.25
                              }}
                            >
                              {spaceData.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Action buttons in header */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 1 },
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                        width: { xs: '100%', sm: 'auto' },
                      }}>
                        {/* Settings button - only show if user has manage_collections permission */}
                        {spaceData.user_permission?.includes('manage_collections') && (
                          <Tooltip title="Space Settings" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                dispatch({
                                  type: "SET_COLLABORATORS_DIALOG_OPEN",
                                  payload: { collobartorsDialogOpen: true },
                                });
                              }}
                              sx={{
                                color: 'text.secondary',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  color: 'primary.main'
                                },
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                              }}
                            >
                              <SettingsOutlined sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Analytics button - only show if user has manage_collections permission */}
                        {spaceData.user_permission?.includes('manage_collections') && (
                          <Tooltip title="View Analytics" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setAnalyticsDialogOpen(true);
                              }}
                              sx={{
                                color: 'text.secondary',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  color: 'primary.main'
                                },
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                              }}
                            >
                              <BarChartIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {token && user && (
                          <Tooltip title={isSubscribed ? 'Unsubscribe from space' : 'Subscribe to space'} arrow>
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                if (!token || !user) {
                                  dispatch({
                                    type: "SET_POPUP",
                                    payload: {
                                      text: "Please log in to subscribe to spaces",
                                      type: "error",
                                    },
                                  });
                                  setTimeout(() => {
                                    dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                                  }, popupTimer);
                                  return;
                                }

                                try {
                                  setIsCheckingSubscription(true);

                                  const newSubscribeValue = !isSubscribed;

                                  const response = await axios.post(`${API_URL}/spaces/authenticated/spaces/${id}/subscribe`, {
                                    subscribe: newSubscribeValue
                                  }, {
                                    withCredentials: true,
                                  });

                                  const newSubscriptionStatus = response.data.subscribed;
                                  setIsSubscribed(newSubscriptionStatus);

                                  dispatch({
                                    type: "SET_POPUP",
                                    payload: {
                                      text: newSubscriptionStatus ? "Subscribed successfully" : "Unsubscribed successfully",
                                      type: "success",
                                    },
                                  });
                                  setTimeout(() => {
                                    dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                                  }, popupTimer);

                                } catch (err) {
                                  console.error('Subscription error:', err);
                                  dispatch({
                                    type: "SET_POPUP",
                                    payload: {
                                      text: err?.response?.data?.error || "Failed to update subscription. Please try again.",
                                      type: "error",
                                    },
                                  });
                                  setTimeout(() => {
                                    dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                                  }, popupTimer);
                                } finally {
                                  setIsCheckingSubscription(false);
                                }
                              }}
                              disabled={isCheckingSubscription}
                              sx={{
                                color: isSubscribed ? '#d32f2f' : '#2e7d32',
                                backgroundColor: isSubscribed ? 'rgba(211, 47, 47, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                                '&:hover': {
                                  backgroundColor: isSubscribed ? 'rgba(211, 47, 47, 0.15)' : 'rgba(46, 125, 50, 0.15)',
                                },
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                              }}
                            >
                              {isCheckingSubscription ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : isSubscribed ? (
                                <NotificationsOffIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              ) : (
                                <NotificationsActiveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                        {user && (spaceData.user_name === user.user_name) && (
                          <Tooltip title="Edit Space Details" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setIsDescriptionExpanded(true);
                                setTriggerEdit(true);
                              }}
                              sx={{
                                color: 'text.secondary',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  color: 'primary.main'
                                },
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* View Mode Toggle */}
                        <Tooltip title="Toggle view mode" arrow>
                          <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            size="small"
                            sx={{
                              ml: { xs: 0, sm: 1 },
                              '& .MuiToggleButton-root': {
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                  }
                                }
                              }
                            }}
                          >
                            <ToggleButton value="grid" aria-label="grid view">
                              <ViewModuleIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            </ToggleButton>
                            <ToggleButton value="feed" aria-label="feed view">
                              <ViewListIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Tooltip>

                        <Tooltip title={isDescriptionExpanded ? 'Collapse details' : 'View details'} arrow>
                          <IconButton
                            size="small"
                            sx={{
                              color: 'primary.main',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                                transform: 'scale(1.05)'
                              },
                              transition: 'all 0.2s ease-in-out',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 }
                            }}
                          >
                            {isDescriptionExpanded ? (
                              <KeyboardArrowUpIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                            ) : (
                              <KeyboardArrowDownIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    {isDescriptionExpanded && (
                      <Box sx={{ 
                        p: 0,
                        backgroundColor: 'rgba(25, 118, 210, 0.01)',
                        borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <SpaceDescription
                          spaceData={spaceData}
                          spaceId={id}
                          isSubscribed={isSubscribed}
                          isCheckingSubscription={isCheckingSubscription}
                          onSubscriptionChange={checkSubscriptionStatus}
                          onImageUpload={handleImageUpload}
                          onVideoUpload={handleVideoUpload}
                          onRemoveImage={handleRemoveImage}
                          onSave={handleChangeSpace}
                          disabled={!(user && (spaceData.user_name === user.user_name))}
                          triggerEdit={triggerEdit}
                          onEditTriggered={() => setTriggerEdit(false)}
                          onRefreshSpace={getSpace}
                          showRecommendations={activeTab === 1}
                          onShowRecommendationsChange={(value) => {
                            // When recommendations are enabled/disabled, switch to appropriate tab
                            if (value) {
                              setActiveTab(1);
                            } else {
                              setActiveTab(0);
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
              
              {/* Modern Tab Interface - 2025 Design - Only show if there are multiple tabs */}
              {(() => {
                const canViewRecommendations = spaceData.user_permission?.includes('manage_collections');
                const shouldShowTabs = canViewRecommendations; // Only show tabs if Recommendations tab is available
                
                return shouldShowTabs ? (
                  <Box sx={{ 
                    mb: isDraggingOver && activeTab === 1 ? 4 : 2.5,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}>
                    <Box sx={{ 
                      position: 'relative',
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(25, 118, 210, 0.12)',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                      overflow: 'visible',
                      maxWidth: '500px',
                      flex: 1,
                      minWidth: { xs: 0, sm: '300px' },
                      transition: 'all 0.3s ease',
                    }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                          minHeight: 'unset',
                          '& .MuiTabs-indicator': {
                            height: 2,
                            borderRadius: '2px 2px 0 0',
                            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          },
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: { xs: '0.8rem', sm: '0.95rem' },
                            fontWeight: 500,
                            minHeight: { xs: 40, sm: 46 },
                            py: { xs: 0.5, sm: 1 },
                            px: { xs: 1, sm: 2 },
                            color: 'text.secondary',
                            transition: 'all 0.25s ease-in-out',
                            position: 'relative',
                            borderRadius: '8px',
                            m: 0.5,
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              '& .MuiTab-iconWrapper': {
                                transform: 'scale(1.08)',
                              }
                            },
                            '&.Mui-selected': {
                              color: 'primary.main',
                              fontWeight: 600,
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              '& .MuiTab-iconWrapper': {
                                transform: 'scale(1.1)',
                              }
                            },
                          },
                        }}
                      >
                        <Tab
                          icon={<BookmarkIcon sx={{ 
                            fontSize: 18, 
                            transition: 'all 0.2s ease',
                          }} />}
                          iconPosition="start"
                          label="Saved Items"
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          sx={{
                            '& .MuiTab-iconWrapper': {
                              mr: 1,
                              transition: 'all 0.2s ease',
                            },
                            ...(isDraggingOver && activeTab === 1 && {
                              backgroundColor: 'rgba(25, 118, 210, 0.2) !important',
                              transform: 'scale(1.02)',
                              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                              '&::after': {
                                content: '"Drop to review & add"',
                                position: 'absolute',
                                bottom: -20,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: '0.7rem',
                                color: 'primary.main',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }
                            })
                          }}
                        />
                        {/* Recommendations tab - only show if user has manage_collections permission */}
                        {canViewRecommendations && (
                          <Tab
                            icon={
                              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <AutoAwesomeIcon sx={{ 
                                  fontSize: 18, 
                                  transition: 'all 0.2s ease',
                                  opacity: spaceData?.search_for_recommendations ? 1 : 0.5
                                }} />
                                {/* Notification dot for new recommendations */}
                                {spaceData?.search_for_recommendations && activeTab === 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -4,
                                      right: -4,
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      background: '#ff6b6b',
                                      boxShadow: '0 0 4px rgba(255, 107, 107, 0.8)',
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            iconPosition="start"
                            label="Recommendations"
                            sx={{
                              '& .MuiTab-iconWrapper': {
                                mr: 1,
                                transition: 'all 0.2s ease',
                              },
                              opacity: spaceData?.search_for_recommendations ? 1 : 0.6,
                            }}
                          />
                        )}
                      </Tabs>
                    </Box>
                
                    {/* AI Recommendations Toggle Switch - only show if user has manage_collections permission */}
                    {spaceData.user_permission?.includes('manage_collections') && (
                      <Tooltip title={!(user && (spaceData.user_name === user.user_name)) ? "Only space owner can enable AI recommendations" : ""} arrow>
                        <Box sx={{
                          background: spaceData?.search_for_recommendations 
                            ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)'
                            : 'rgba(255, 255, 255, 0.6)',
                          backdropFilter: 'blur(20px)',
                          borderRadius: '10px',
                          border: spaceData?.search_for_recommendations 
                            ? '1px solid rgba(25, 118, 210, 0.2)'
                            : '1px solid rgba(0, 0, 0, 0.08)',
                          boxShadow: spaceData?.search_for_recommendations
                            ? '0 2px 8px rgba(25, 118, 210, 0.15)'
                            : '0 1px 4px rgba(0,0,0,0.04)',
                          px: 1.5,
                          py: 1.1,
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 'fit-content',
                          overflow: 'visible',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: spaceData?.search_for_recommendations
                              ? '0 4px 12px rgba(25, 118, 210, 0.2)'
                              : '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                          }
                        }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!spaceData?.search_for_recommendations}
                                onChange={handleToggleRecommendations}
                                color="primary"
                                disabled={!(user && (spaceData.user_name === user.user_name))}
                              />
                            }
                            label={
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                ml: 0.5
                              }}>
                                <AutoAwesomeIcon sx={{ 
                                  fontSize: 15, 
                                  color: spaceData?.search_for_recommendations 
                                    ? 'primary.main' 
                                    : 'text.disabled',
                                  transition: 'color 0.3s ease',
                                  filter: spaceData?.search_for_recommendations 
                                    ? 'drop-shadow(0 0 4px rgba(25, 118, 210, 0.4))'
                                    : 'none'
                                }} />
                                <Typography variant="body2" sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                  fontWeight: spaceData?.search_for_recommendations ? 600 : 500,
                                  color: spaceData?.search_for_recommendations
                                    ? 'primary.main'
                                    : 'text.secondary',
                                  transition: 'all 0.3s ease',
                                  letterSpacing: '0.01em',
                                  whiteSpace: 'nowrap',
                                }}>
                                  AI Recommendations
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              m: 0,
                              '& .MuiFormControlLabel-label': {
                                marginLeft: '4px',
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                ) : null;
              })()}
              
              <SpaceRates 
                ref={spaceRatesRef}
                spaceId={id}
                parentSpaceId={spaceData?.parent_space_id}
                onAiFilterApplied={handleAiFilterApplied}
                defaultShowRecommendations={spaceData?.search_for_recommendations !== undefined ? !!spaceData.search_for_recommendations : false}
                showRecommendations={activeTab === 1 && spaceData.user_permission?.includes('manage_collections')}
                onShowRecommendationsChange={(value) => {
                  // When recommendations are enabled/disabled, switch to appropriate tab
                  // Only allow if user has manage_collections permission
                  if (value && spaceData.user_permission?.includes('manage_collections')) {
                    setActiveTab(1);
                  } else {
                    setActiveTab(0);
                  }
                }}
                viewMode={viewMode}
                spaceData={spaceData}
                // If you need removing items from space, pass a callback:
                // onRemoveFromSpace={handleRemoveFromSpace} 
              />
              </Box>
            </>
          )}
        </Box>
      </Box>


      {/* AI Chat Component */}
      {aiChatOpen && (
        <ChatComponent
          onResponse={handleAiChatResponse}
        />
      )}

      {/* Settings for collaborators, etc. - only show if user has manage_collections permission */}
      {collobartorsDialogOpen && spaceData.user_permission?.includes('manage_collections') && (
        <SettingsDialog
          subSpaces={subSpaces}
          open={collobartorsDialogOpen}
          onChangeSpace={handleChangeSpace}
          spaceData={spaceData}
          onDeleteSpaceClick={() =>
            dispatch({
              type: "SET_DELETE_SPACE_DIALOG",
              payload: { deleteSpaceDialogOpen: true },
            })
          }
          onClose={() =>
            dispatch({
              type: "SET_COLLABORATORS_DIALOG_OPEN",
              payload: { collobartorsDialogOpen: false },
            })
          }
          onAddCollaboratorClick={addCollaborator}
          onDeleteCollaboratorClick={deleteCollaborators}
          collaborators={spaceData.collaborators !== undefined ? collaborators : []}
          getSpace={getSpace}
        />
      )}

      {/* Confirm Deletion of the entire space */}
      <ConfirmationDialog
        open={deleteSpaceDialogOpen}
        onClose={() =>
          dispatch({
            type: "SET_DELETE_SPACE_DIALOG",
            payload: { deleteSpaceDialogOpen: false },
          })
        }
        onConfirm={() => {
          deleteSpace();
          dispatch({
            type: "SET_DELETE_SPACE_DIALOG",
            payload: { deleteSpaceDialogOpen: false },
          });
        }}
        title={"Delete space"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={
          spaceData?.has_children || (subSpaces && subSpaces.length > 0)
            ? "Are you sure you want to delete this space? This will permanently delete this space and all its sub-collections. This action cannot be undone."
            : "Are you sure you want to delete this space? This action cannot be undone."
        }
      />

      {/* AI Summary Editor – if you want to open a large editor popup */}
      <AiSummaryEditor
        open={summaryDialogOpen}
        onPerform={performAiAction}
        onClose={() => {
          setSummaryDialogOpen(false);
          setSummary("");
        }}
        defaultValue={"Hey, how can I help you?"}
        value={summary}
      />

      {/* RatingObject Dialog for Drag & Drop */}
      {showRatingObjectDialog && draggedItemData && (
        <RatingObject
          open={showRatingObjectDialog}
          handleClose={() => {
            setShowRatingObjectDialog(false);
            setDraggedItemData(null);
          }}
          id={draggedItemData.rated_object_id || draggedItemData.object_id || draggedItemData._id || draggedItemData.id}
          initialData={draggedItemData}
          preselectedSpaceId={id}
          preselectedParentSpaceId={spaceData?.parent_space_id}
          onRateSuccessfully={() => {
            setShowRatingObjectDialog(false);
            setDraggedItemData(null);
            // Switch to Saved Items tab to show the newly added item
            setActiveTab(0);
            // Refresh the space data
            dispatch({ type: "GET_RATES", payload: true });
          }}
        />
      )}

      {/* Analytics Dialog - only show if user has manage_collections permission */}
      {spaceData.user_permission?.includes('manage_collections') && (
        <AnalyticsDialog
          open={analyticsDialogOpen}
          onClose={() => setAnalyticsDialogOpen(false)}
          spaceId={id}
          spaceData={spaceData}
        />
      )}
    </>
  );
};

export default Space;
